import {useRenderedLocation} from '@github-ui/react-router'
import {useMemo} from 'react'

import type {ViewConfiguration, ViewConfigurationOverrides} from '../../contexts/views/view-configuration'
import {readRequestedAxes, readVisibleFieldsOverride} from '../../utils/saved-view-url-params'
import {Capability, type FieldsData} from './fields'
import {getFieldsForCapability} from './get-fields-for-capability'
import {useSavedViewUrlActions} from './use-saved-view-url-actions'

interface UseSavedViewUrlConfigurationInput {
  /** The configuration the server rendered against, which already includes axis overrides. */
  applied: ViewConfiguration
  /** Saved settings, or the host's defaults when nothing is saved. URL params are written relative to it. */
  baseline: ViewConfiguration
  /** Once loaded, `visible_fields` entries the view does not offer as attributes are ignored. */
  fields?: FieldsData
  /** The search of a navigation on this page that is still loading. */
  pendingSearch?: string
}

/**
 * Reads and writes a saved view's configuration in the URL. Returns the props a host passes to
 * `SavedViewConfigurationProvider`.
 *
 * Axis overrides (`layout`, `group_by`, `swimlane_by`) reach the server, so `applied` already
 * reflects them. Visible fields stay client-side (outside the route's query key, so toggling
 * one does not refetch) and are read from the URL here.
 *
 * While `pendingSearch` loads, the configuration it asks for stands in for `applied`, so a pick
 * shows right away and the next pick builds on it. The server answer replaces it on arrival.
 */
export function useSavedViewUrlConfiguration({
  applied,
  baseline,
  fields,
  pendingSearch,
}: UseSavedViewUrlConfigurationInput): {
  appliedConfiguration: ViewConfiguration
  onConfigurationChange: (changes: ViewConfigurationOverrides) => void
  onReset: () => void
  /** The URL `onConfigurationChange` navigates to for `changes`, or `null` when they change nothing. */
  urlForChanges: (changes: ViewConfigurationOverrides) => string | null
} {
  // TanStack Router reports the pending search from `useLocation`, which this compares `pendingSearch` against.
  const {search: locationSearch} = useRenderedLocation()
  const search = pendingSearch ?? locationSearch

  const requested = useMemo(
    () =>
      pendingSearch === undefined
        ? applied
        : readRequestedAxes(applied, baseline, new URLSearchParams(locationSearch), new URLSearchParams(pendingSearch)),
    [applied, baseline, locationSearch, pendingSearch],
  )
  const knownFields = useMemo(
    () =>
      fields
        ? new Set(getFieldsForCapability(fields, Capability.ATTRIBUTABLE).map(field => field.identifier))
        : undefined,
    [fields],
  )
  const urlVisibleFields = useMemo(
    () => readVisibleFieldsOverride(new URLSearchParams(search), requested.layout, knownFields),
    [requested.layout, knownFields, search],
  )
  const appliedConfiguration = useMemo(
    () => (urlVisibleFields ? {...requested, visibleFields: urlVisibleFields} : requested),
    [requested, urlVisibleFields],
  )

  const {applyUrlChanges, urlForChanges, clearOverrides} = useSavedViewUrlActions({
    baseline,
    current: requested,
    search,
  })

  return {appliedConfiguration, onConfigurationChange: applyUrlChanges, onReset: clearOverrides, urlForChanges}
}
